<template>
  <div>
    <section-collapseable
        title="Contacts"
        :sub-title="contacts.length.toString()"
        collapseable
        button-text="Contact"
        @add="addContact"
    >
      <div class="columns">
        <contact-item
          v-if="primaryContact"
          class="column is-4"
          :contact="primaryContact"
          :record="record"
          :is-primary="true"
          :is-buyer="isPrimaryAndBuyer"
          :is-seller="isPrimaryAndSeller"
          @edit="openEditContactLeadRelationship"
        ></contact-item>

        <contact-item
          v-if="showBuyer"
          class="column is-4"
          :contact="buyerContact"
          :record="record"
          :is-buyer="true"
          @edit="openEditContactLeadRelationship"
        ></contact-item>

        <contact-item
          v-if="showSeller"
          class="column is-4"
          :contact="sellerContact"
          :record="record"
          :is-seller="true"
          @edit="openEditContactLeadRelationship"
        ></contact-item>
      </div>

      <!-- Expanded Section -->
      <template v-slot:expanded>
        <div class="columns is-multiline">
          <contact-item
            v-for="contact in otherContacts"
            :key="contact.id"
            class="column is-4"
            :contact="contact"
            :record="record"
            @edit="openEditContactLeadRelationship"
          ></contact-item>
        </div>
      </template>

      <modal-container>
        <modal-default
          ref="EditContactLeadRelationship"
          title="Edit Contact/Lead Relationship"
        >
          <form-field-select
            label="Role this contact has on the lead?"
            v-model="roleForm.role"
            :validation-errors="validationErrors.role"
          >
            <option v-for="option in ENUMS.RecordRoleEnum" :value="option.value">{{ option.label }}</option>
          </form-field-select>

          <form-field-switch
            label="Primary Contact?"
            v-model="roleForm.is_primary"
            :validation-errors="validationErrors.is_primary"
          ></form-field-switch>

          <template v-slot:footer>
            <button-link
              @click="removeContactFromRecord"
              :loading="removingContactRelationship"
              :disabled="removingContactRelationship"
            >Remove Contact from Lead
            </button-link>

            <button-default
              class="ml-auto"
              @click="updateContactRole"
              :loading="updatingContactRelationship"
              :disabled="updatingContactRelationship"
            >Update Role
            </button-default>
          </template>
        </modal-default>
      </modal-container>
    </section-collapseable>
  </div>
</template>

<script>
import ContactItem from "../components/ContactItem";
import AttachContactToLeadModal from "../components/AttachContactToLeadModal";
import {appendModal} from "../../../services/modals";
import {ENUMS} from "../../../services/enums";

export default {
  name: "LeadContacts",
  components: {ContactItem},
  data() {
    return {
      removingContactRelationship: false,
      updatingContactRelationship: false,
      roleForm: {
        id: this.$store.state.activeRecord.record.id,
        validate_duplicate: false,
        role: 0,
        is_primary: false,
        is_primary_record: false,
        contact_id: null,
      },
      contact: {},
      validationErrors: {},
    }
  },
  computed: {
    record() {
      // If activeRecord is null then return necessary empty info
      return this.$store.state.activeRecord ? this.$store.state.activeRecord.record : {
        primary_contact_id: null,
        buyer_id: null,
        seller_id: null,
      };
    },
    contacts() {
      // If activeRecord is null then return necessary empty info
      return this.$store.state.activeRecord ? this.$store.state.activeRecord.contacts : [];
    },
    primaryContact() {
      return this.contacts.find(contact => contact.id === this.record.primary_contact_id);
    },
    buyerContact() {
      return this.contacts.find(contact => contact.id === this.record.buyer_id);
    },
    sellerContact() {
      return this.contacts.find(contact => contact.id === this.record.seller_id);
    },
    isPrimaryAndBuyer() {
      return this.primaryContact && this.buyerContact && this.primaryContact.id === this.buyerContact.id;
    },
    isPrimaryAndSeller() {
      return this.primaryContact && this.sellerContact && this.primaryContact.id === this.sellerContact.id;
    },
    showBuyer() {
      if (this.buyerContact && this.primaryContact && this.buyerContact.id === this.primaryContact.id) {
        // Already displayed via the Primary Contact
        return false;
      }

      return !!this.buyerContact;
    },
    showSeller() {
      if (this.sellerContact && this.primaryContact && this.sellerContact.id === this.primaryContact.id) {
        // Already displayed via the Primary Contact
        return false;
      }

      return !!this.sellerContact;

    },
    otherContacts() {
      return this.contacts.filter(contact => {
        return !(
            contact.id === this.record.primary_contact_id ||
            contact.id === this.record.buyer_id ||
            contact.id === this.record.seller_id
        );
      });
    },
  },
  methods: {
    addContact() {
      appendModal(AttachContactToLeadModal, this, {record: this.record});
    },
    openEditContactLeadRelationship(contact) {
      const record = this.$store.state.activeRecord.record;
      this.contact = contact;

      this.roleForm.contact_id = contact.id;
      this.roleForm.is_primary = contact.id === record.primary_contact_id;
      this.roleForm.is_primary_record = contact.primary_record_id === record.id;

      if (contact.id === record.buyer_id) {
        this.roleForm.role = ENUMS.RecordRoleEnum.Buyer.value;
      } else if (contact.id === record.seller_id) {
        this.roleForm.role = ENUMS.RecordRoleEnum.Seller.value;
      } else {
        this.roleForm.role = ENUMS.RecordRoleEnum.None.value;
      }

      this.$refs.EditContactLeadRelationship.open();
    },
    updateContactRole() {
      this.updatingContactRelationship = true;

      this.$api.records.attachContact(this.roleForm)
        .then(() => {
          this.$buefy.toast.open({
            message: "Relationship Updated",
            type: "is-success"
          });

          this.$refs.EditContactLeadRelationship.close();
        }).finally(() => {
        this.contact = {};
        this.updatingContactRelationship = false;
      });
    },
    removeContactFromRecord() {
      this.removingContactRelationship = true;
      const recordId = this.$store.state.activeRecord.record.id;

      return this.$utils.confirm(
        "Are you sure you want to remove " + this.contact.full_name + " form this Lead?",
        "Remove Contact From Lead"
      )
        .then(() => {
          return this.$api.records.removeContact(recordId, this.contact.id)
            .then(() => {
              this.$refs.EditContactLeadRelationship.close();
            })
            .finally(() => {
              this.contact = {};
              this.removingContactRelationship = false;
            });
        });
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

</style>
